import React, { useEffect, useRef, useState } from 'react';
import { formatPerformanceValue } from './performanceTimelineModel';
import { formatPlatformMetricValue } from './platformAnalyticsProfiles';
import './DeliveryChart.css';
import './PerformanceTimeline.css';
function Marker({
  shape,
  x,
  y,
  color,
  size = 3
}) {
  const shared = {
    fill: color,
    stroke: '#081323',
    strokeWidth: 1
  };
  if (shape === 'square') return <rect x={x - size} y={y - size} width={size * 2} height={size * 2} {...shared} />;
  if (shape === 'diamond') return <path d={`M${x},${y - size - 1} L${x + size + 1},${y} L${x},${y + size + 1} L${x - size - 1},${y}Z`} {...shared} />;
  if (shape === 'triangle') return <path d={`M${x},${y - size - 1} L${x + size + 1},${y + size} L${x - size - 1},${y + size}Z`} {...shared} />;
  return <circle cx={x} cy={y} r={size} {...shared} />;
}
function Swatch({
  metric
}) {
  return <svg width="28" height="14" viewBox="0 0 28 14" aria-hidden="true"><path d="M0 7H28" stroke={metric.color} strokeWidth="2" strokeDasharray={metric.dash} /><Marker shape={metric.marker} x={14} y={7} color={metric.color} /></svg>;
}
const ceiling = value => {
  if (!(value > 0)) return 1;
  const power = 10 ** Math.floor(Math.log10(value));
  const normalized = value / power;
  return (normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10) * power;
};
const dayLabel = date => new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC'
}).format(new Date(date + 'T00:00:00Z'));
export default function PerformanceTimeline({
  model,
  BarComponent
}) {
  const root = useRef(null);
  const [width, setWidth] = useState(900);
  const [hidden, setHidden] = useState([]);
  const [reading, setReading] = useState(null);
  useEffect(() => {
    const measure = () => setWidth(Math.max(200, root.current?.getBoundingClientRect().width || 900));
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(root.current);
    return () => observer.disconnect();
  }, []);
  // A refreshed/scope-replaced model cannot leave a reading on an old date.
  useEffect(() => {
    setReading(null);
  }, [model]);
  const visible = model.series.filter(metric => !hidden.includes(metric.key));
  const maxFor = unit => ceiling(Math.max(0, ...visible.filter(s => s.unit === unit).flatMap(s => model.dates.map(d => s.values[d] ?? 0))) * 1.05);
  const maxima = {
    count: maxFor('count'),
    currency: maxFor('currency')
  };
  const narrow = width < 560;
  const leftUnit = model.series.find(metric => metric.key === model.primaryKey)?.unit === 'currency' ? 'currency' : 'count';
  const rightUnit = leftUnit === 'currency' ? 'count' : 'currency';
  const axisValue = (unit, value) => formatPlatformMetricValue(unit, value, { axis: true });
  const gutter = unit => Math.max(narrow ? 46 : 62, axisValue(unit, maxima[unit]).length * 6.4 + 18);
  const left = gutter(leftUnit), right = gutter(rightUnit);
  const top = 32,
    height = narrow ? 220 : 300,
    bottom = top + height;
  const plotWidth = Math.max(80, width - left - right);
  const slot = plotWidth / model.dates.length;
  const x = i => left + slot * (i + 0.5);
  const y = (metric, value) => bottom - value / maxima[metric.unit] * height;
  const ticks = narrow ? [0, 0.5, 1] : [0, 0.25, 0.5, 0.75, 1];
  const hasCounts = visible.some(s => s.unit === 'count');
  const hasSpend = visible.some(s => s.unit === 'currency');
  const hasUnit = unit => unit === 'currency' ? hasSpend : hasCounts;
  const axisTitle = unit => unit === 'currency' ? 'Spend (USD)' : 'Counts';
  const maxLabels = Math.max(2, Math.floor(plotWidth / 75));
  const stride = Math.max(1, Math.ceil(model.dates.length / maxLabels));
  const activeDate = reading == null ? null : model.dates[reading];
  const toggle = key => {
    setHidden(current => current.includes(key) ? current.filter(item => item !== key) : [...current, key]);
    setReading(null);
  };
  return <div className="performance-timeline" data-delivery-chart="time-series" ref={root}>
    <svg className="cpx-chart-svg performance-timeline-plot" width="100%" height={bottom + 38} viewBox={`0 0 ${width} ${bottom + 38}`} role="group" aria-label="Performance timeline; focus or tap a date for exact values" onMouseLeave={() => {
      if (!root.current?.contains(document.activeElement)) setReading(null);
    }}>
      <title>Performance timeline</title><desc>{model.description}</desc>
      {hasUnit(leftUnit) && <text x={left} y="16" className="cpx-chart-axis-label performance-axis-title">{axisTitle(leftUnit)}</text>}
      {hasUnit(rightUnit) && <text x={width - right} y="16" textAnchor="end" className="cpx-chart-axis-label performance-axis-title">{axisTitle(rightUnit)}</text>}
      {ticks.map(tick => <g key={tick}>
        <line x1={left} x2={width - right} y1={bottom - tick * height} y2={bottom - tick * height} className="cpx-chart-gridline" />
        {hasUnit(leftUnit) && <text className="cpx-chart-axis-label" x={left - 8} y={bottom - tick * height + 4} textAnchor="end">{axisValue(leftUnit, tick * maxima[leftUnit])}</text>}
        {hasUnit(rightUnit) && <text className="cpx-chart-axis-label" x={width - right + 8} y={bottom - tick * height + 4}>{axisValue(rightUnit, tick * maxima[rightUnit])}</text>}
      </g>)}
      {BarComponent && visible.filter(metric => metric.key === model.primaryKey).map(metric => <g key={metric.key} className="performance-primary-bars">
        {model.dates.map((date, i) => {
          const value = metric.values[date];
          if (!(value > 0)) return null;
          const barWidth = Math.max(3, Math.min(34, slot * 0.62));
          const barHeight = Math.max(0.75, bottom - y(metric, value));
          const leftX = x(i) - barWidth / 2, topY = y(metric, value);
          return <BarComponent key={date} x={leftX} y={topY} width={barWidth} height={barHeight} fill={model.primaryColor} />;
        })}
      </g>)}
      {visible.map(metric => {
        let connected = false;
        const path = model.dates.map((date, i) => {
          const value = metric.values[date];
          if (value == null) {
            connected = false;
            return '';
          }
          const command = connected ? 'L' : 'M';
          connected = true;
          return `${command}${x(i)},${y(metric, value)}`;
        }).join(' ');
        return <g key={metric.key} data-series={metric.key} aria-label={`${metric.label}, ${metric.unit === 'currency' ? 'USD' : 'count'}`}>
          <path d={path} fill="none" stroke={metric.color} strokeWidth="2" strokeDasharray={metric.dash} />
          {model.dates.map((date, i) => metric.values[date] == null ? null : <Marker key={date} shape={metric.marker} x={x(i)} y={y(metric, metric.values[date])} color={metric.color} />)}
        </g>;
      })}
      {model.dates.map((date, i) => <g key={date}>
        {(i % stride === 0 || (i === model.dates.length - 1 && i % stride >= Math.ceil(60 / slot))) && <text className="cpx-chart-axis-label" x={x(i)} y={bottom + 24} textAnchor="middle">{dayLabel(date)}</text>}
        <rect x={left + i * slot} y={top} width={slot} height={height} fill={reading === i ? 'rgba(255,255,255,.05)' : 'transparent'} tabIndex={0} role="button" aria-label={`${date}: ${visible.map(s => `${s.label} ${formatPerformanceValue(s.unit, s.values[date])}`).join('; ')}`} onMouseEnter={() => {
          if (!document.activeElement?.matches('.performance-timeline-plot rect[role="button"]')) setReading(i);
        }} onFocus={() => setReading(i)} onBlur={() => setReading(null)} onClick={() => setReading(i)} onKeyDown={event => {
          if (event.key === 'Escape') {
            setReading(null);
            event.currentTarget.blur();
          }
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setReading(i);
          }
          if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
            event.preventDefault();
            const nodes = root.current.querySelectorAll('rect[role="button"]');
            nodes[(i + (event.key === 'ArrowRight' ? 1 : nodes.length - 1)) % nodes.length].focus();
          }
        }} />
      </g>)}
    </svg>
    <div className="cpx-legend performance-timeline-legend" role="group" aria-label="Performance metrics">
      {visible.some(metric => metric.key === model.primaryKey) && <span className="cpx-legend-item"><span className="cpx-swatch" style={{ background: model.primaryColor }} />{model.platformLabel} · {model.series.find(metric => metric.key === model.primaryKey)?.label || model.primaryKey}</span>}
      {model.series.map(metric => <button type="button" className="cpx-legend-item" key={metric.key} aria-pressed={!hidden.includes(metric.key)} aria-label={`${hidden.includes(metric.key) ? 'Show' : 'Hide'} ${metric.label}`} onClick={() => toggle(metric.key)}><Swatch metric={metric} /><span>{metric.label}</span></button>)}
    </div>
    {!visible.length && <p role="status">All metrics hidden. Select a legend item to show it.</p>}
    {activeDate && <div className="cpx-chart-tooltip performance-timeline-reading" role="status" aria-live="polite"><strong className="cpx-chart-tooltip-title">{activeDate}</strong><div className="cpx-chart-tooltip-list">{visible.map(metric => <span className="cpx-legend-item" key={metric.key}><Swatch metric={metric} /><span>{metric.label}</span><b>{formatPerformanceValue(metric.unit, metric.values[activeDate])}</b></span>)}{(model.extraRowsByDate?.[activeDate] || []).map(row => <span className="cpx-legend-item" key={row.key}><span>{row.label}</span><b>{row.formattedValue}</b></span>)}</div></div>}
    {model.series.some(metric => metric.partial) && <p className="performance-timeline-hint">Partial dated coverage: {model.series.filter(metric => metric.partial).map(metric => `${metric.label} ${formatPerformanceValue(metric.unit, metric.datedTotal)} of ${formatPerformanceValue(metric.unit, metric.total)}`).join('; ')}. Remaining delivery has no reported date.</p>}
    {model.unavailable.length > 0 && <details className="performance-timeline-unavailable"><summary>Unavailable daily metrics ({model.unavailable.length})</summary>{model.unavailable.map(metric => <p key={metric.key}>{metric.label}: {metric.reason}.</p>)}</details>}
  </div>;
}
